import {
  useFloating,
  FloatingPortal,
  autoUpdate,
  useDismiss,
  useInteractions,
} from '@floating-ui/react';
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/20/solid';
import {
  CalendarDaysIcon,
  ChevronDownIcon,
  XMarkIcon,
  ArrowLeftIcon,
} from '@heroicons/react/24/outline';
import classNames from 'classnames';
import {
  format,
  addMonths,
  subMonths,
  getDay,
  startOfMonth,
  endOfMonth,
  eachDayOfInterval,
  isSameDay,
  isToday,
  isAfter,
  isBefore,
  addDays,
  parseISO,
  startOfDay,
} from 'date-fns';
import React, { useState, useRef } from 'react';
import { useField } from 'react-final-form';

interface EnhancedDateRangePickerProps {
  meta: {
    touched: boolean;
    error: string;
  };
  minDate?: Date;
  className?: string;
}

interface TailwindCalendarProps {
  date: Date;
  onChange: (date: Date | null) => void;
  minDate?: Date;
  startDate: Date | null;
  endDate: Date | null;
}

// Helper function to normalize date by removing time component
const normalizeDate = (date: Date): Date => {
  return startOfDay(date);
};

const generateDays = (currentMonth: Date) => {
  const monthStart = startOfMonth(currentMonth);
  const monthEnd = endOfMonth(currentMonth);

  const daysInMonth = eachDayOfInterval({ start: monthStart, end: monthEnd });

  const firstDayOfWeek = getDay(monthStart);

  const adjustedFirstDay = firstDayOfWeek === 0 ? 6 : firstDayOfWeek - 1;

  const emptyDaysBefore = Array(adjustedFirstDay).fill(null);

  const totalCells = Math.ceil((daysInMonth.length + adjustedFirstDay) / 7) * 7;
  const emptyDaysAfter = Array(
    totalCells - daysInMonth.length - adjustedFirstDay,
  ).fill(null);

  return [...emptyDaysBefore, ...daysInMonth, ...emptyDaysAfter];
};

const isDateInRange = (
  day: Date | null,
  startDate: Date | null,
  endDate: Date | null,
) => {
  if (!day || !startDate || !endDate) return false;
  return isAfter(day, startDate) && isBefore(day, endDate);
};

const TailwindCalendar = ({
  date,
  onChange,
  minDate,
  startDate,
  endDate,
}: TailwindCalendarProps) => {
  const [currentMonth, setCurrentMonth] = useState(date || new Date());

  const goToPreviousMonth = () => {
    setCurrentMonth(subMonths(currentMonth, 1));
  };

  const goToNextMonth = () => {
    setCurrentMonth(addMonths(currentMonth, 1));
  };

  const days = generateDays(currentMonth);

  const handleDateClick = (day: Date | null) => {
    if (!day) return;

    if (minDate) {
      const normalizedDay = normalizeDate(day);
      if (normalizedDay < normalizeDate(minDate)) {
        return;
      }
    }

    onChange(day);
  };

  return (
    <div className="w-full">
      <div className="flex items-center">
        <h2 className="flex-auto text-base font-semibold text-gray-900">
          {format(currentMonth, 'MMMM yyyy')}
        </h2>
        <button
          type="button"
          onClick={goToPreviousMonth}
          className={classNames(
            '-my-1.5 flex flex-none items-center justify-center p-2',
            'text-gray-400 hover:text-gray-500',
          )}
        >
          <span className="sr-only">Previous month</span>
          <ChevronLeftIcon className="size-6" aria-hidden="true" />
        </button>
        <button
          type="button"
          onClick={goToNextMonth}
          className="-my-1.5 -mr-1.5 ml-2 flex flex-none items-center justify-center p-2 text-gray-400 hover:text-gray-500"
        >
          <span className="sr-only">Next month</span>
          <ChevronRightIcon className="size-6" aria-hidden="true" />
        </button>
      </div>
      <div className="mt-6 grid grid-cols-7 text-center text-xs text-gray-500">
        <div>M</div>
        <div>T</div>
        <div>W</div>
        <div>T</div>
        <div>F</div>
        <div>S</div>
        <div>S</div>
      </div>
      <div className="mt-2 grid grid-cols-7 text-sm">
        {days.map((day, dayIdx) => {
          if (day === null) {
            return (
              <div
                key={`empty-${dayIdx}`}
                className={classNames(
                  dayIdx > 6 ? 'border-t border-gray-200' : '',
                  'py-2',
                )}
              >
                <div className="mx-auto size-8"></div>
              </div>
            );
          }

          const isSelected =
            (startDate && isSameDay(day, startDate)) ||
            (endDate && isSameDay(day, endDate));
          const isRangeStart = startDate && isSameDay(day, startDate);
          const isRangeEnd = endDate && isSameDay(day, endDate);
          const isInRange = isDateInRange(day, startDate, endDate);
          const isDisabled = minDate
            ? normalizeDate(day) < normalizeDate(minDate)
            : false;

          return (
            <div
              key={format(day, 'yyyy-MM-dd')}
              className={classNames(
                dayIdx > 6 ? 'border-t border-gray-200' : '',
                'py-2',
              )}
            >
              <button
                type="button"
                onClick={() => handleDateClick(day)}
                disabled={isDisabled}
                className={classNames(
                  isSelected ? 'text-white' : '',
                  !isSelected && isToday(day) ? 'text-primaryBlue' : '',
                  !isSelected && !isToday(day) && !isDisabled
                    ? 'text-gray-900'
                    : '',
                  !isSelected && isDisabled ? 'text-gray-300' : '',
                  isRangeStart ? 'bg-primaryBlue' : '',
                  isRangeEnd ? 'bg-primaryBlue' : '',
                  isInRange && !isSelected ? 'bg-blue-50 text-primaryBlue' : '',
                  (!isSelected &&
                    !isDisabled &&
                    !isInRange &&
                    'hover:bg-gray-100') ||
                    '',
                  ((isSelected || isToday(day)) && 'font-semibold') || '',
                  isDisabled ? 'cursor-not-allowed' : '',
                  'mx-auto flex size-8 items-center justify-center rounded-full',
                )}
              >
                <time dateTime={format(day, 'yyyy-MM-dd')}>
                  {format(day, 'd')}
                </time>
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const EnhancedDateRangePicker: React.FC<EnhancedDateRangePickerProps> = ({
  meta,
  minDate = new Date(),
  className,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectingStart, setSelectingStart] = useState(true);
  const containerRef = useRef<HTMLDivElement>(null);
  const { input: startInput } = useField('checkInDate');
  const { input: endInput } = useField('checkOutDate');

  const { refs, floatingStyles, context } = useFloating({
    whileElementsMounted: autoUpdate,
    open: isOpen,
    onOpenChange: setIsOpen,
    placement: 'bottom-start',
  });
  const dismiss = useDismiss(context);
  const { getReferenceProps, getFloatingProps } = useInteractions([dismiss]);

  const startDate = startInput.value
    ? normalizeDate(parseISO(startInput.value))
    : null;

  const endDate = endInput.value
    ? normalizeDate(parseISO(endInput.value))
    : null;

  const setStartDate = (date: Date | null) => {
    if (date) {
      startInput.onChange(format(date, 'yyyy-MM-dd'));
    } else {
      startInput.onChange('');
    }
  };

  const setEndDate = (date: Date | null) => {
    if (date) {
      endInput.onChange(format(date, 'yyyy-MM-dd'));
    } else {
      endInput.onChange('');
    }
  };

  const onStartDateChange = (date: Date | null) => {
    if (date) {
      startInput.onChange(format(date, 'yyyy-MM-dd'));
    } else {
      startInput.onChange('');
    }
  };

  const onEndDateChange = (date: Date | null) => {
    if (date) {
      endInput.onChange(format(date, 'yyyy-MM-dd'));
    } else {
      endInput.onChange('');
    }
  };

  const handleStartDateChange = (date: Date | null) => {
    setStartDate(date);
    onStartDateChange(date);
    if (date && (!endDate || date >= endDate)) {
      setEndDate(null);
      onEndDateChange(null); // Clear the end date if it's not set or if the start date is after or equal to the end date
    }
    setSelectingStart(false);
  };

  const handleEndDateChange = (date: Date | null) => {
    setEndDate(date);
    onEndDateChange(date);
  };

  const formatDateDisplay = (date: Date | null) => {
    if (!date) return '';
    return format(date, 'MMM d, yyyy');
  };

  const renderDropdown = () => {
    if (!isOpen) return null;
    const referenceWidth =
      refs.reference.current?.getBoundingClientRect()?.width;

    return (
      <FloatingPortal>
        <div
          ref={refs.setFloating}
          className="absolute z-50 rounded-md bg-white p-4 shadow-lg ring-1 ring-black/5 focus:outline-none sm:p-6"
          style={{
            ...floatingStyles,
            width: referenceWidth,
          }}
          {...getFloatingProps()}
        >
          <div className="mb-4 flex items-center justify-between">
            {selectingStart ? (
              <div className="w-5"></div>
            ) : (
              <button
                type="button"
                onClick={() => setSelectingStart(true)}
                className="text-gray-700 hover:text-gray-900"
              >
                <ArrowLeftIcon className="size-5" aria-hidden="true" />
              </button>
            )}
            <p className="grow text-center text-base font-medium text-gray-900">
              {selectingStart
                ? 'Select a check-in date'
                : 'Select a check-out date'}
            </p>
            <button
              type="button"
              onClick={() => setIsOpen(false)}
              className="text-gray-400 hover:text-gray-600"
            >
              <XMarkIcon className="size-5" aria-hidden="true" />
            </button>
          </div>
          <div className="mb-4 border-b border-gray-200"></div>
          <div className="flex flex-col space-y-6 sm:flex-row sm:space-x-12 sm:space-y-0">
            <div className="w-full min-w-[250px] sm:min-w-[280px]">
              <TailwindCalendar
                date={
                  selectingStart
                    ? startDate || new Date()
                    : endDate ||
                      (startDate ? addDays(startDate, 1) : new Date())
                }
                onChange={
                  selectingStart ? handleStartDateChange : handleEndDateChange
                }
                minDate={selectingStart ? minDate : startDate || minDate}
                startDate={startDate}
                endDate={endDate}
              />
            </div>
          </div>
          <div className="mt-6 flex justify-between sm:mt-8">
            <button
              type="button"
              onClick={() => {
                setStartDate(null);
                setEndDate(null);
                onStartDateChange(null);
                onEndDateChange(null);
                setSelectingStart(true);
              }}
              className="text-sm text-gray-600 hover:text-gray-900"
            >
              Clear
            </button>
            <button
              type="button"
              onClick={() => setIsOpen(false)}
              className="rounded-md bg-primaryBlue px-3 py-1.5 text-sm text-white hover:bg-blue-700 sm:px-4 sm:py-2"
            >
              Apply
            </button>
          </div>
        </div>
      </FloatingPortal>
    );
  };

  return (
    <div className={`relative ${className}`} ref={containerRef}>
      <button
        type="button"
        aria-label="select dates"
        onClick={() => {
          setIsOpen(!isOpen);
        }}
        className="flex w-full items-center justify-between rounded-md border border-gray-300 bg-white px-3 py-2 text-sm shadow-sm hover:bg-gray-50"
        ref={refs.setReference}
        {...getReferenceProps()}
      >
        <div className="flex items-center">
          <CalendarDaysIcon className="mr-2 size-5 text-gray-400" />
          <span className="text-gray-700">
            {startDate && endDate
              ? `${formatDateDisplay(startDate)} - ${formatDateDisplay(endDate)}`
              : 'Select dates'}
          </span>
        </div>
        <ChevronDownIcon className="size-5 text-gray-400" />
      </button>

      {renderDropdown()}
      <input
        type="hidden"
        autoComplete="off"
        name={startInput.name}
        value={startInput.value}
      />

      <input
        type="hidden"
        autoComplete="off"
        name={endInput.name}
        value={endInput.value}
      />

      {meta.touched && meta.error && (
        <div className="mt-1 text-sm text-red-500">{meta.error}</div>
      )}
    </div>
  );
};

export default EnhancedDateRangePicker;
